/**
 * URL and neighbourhood helpers for the public gear pages (plan item S24,
 * ADR 011 amendment 2026-09-17). Pure functions of a product list: the shipped
 * catalogue is passed in by the caller (`catalog.ts` wraps them over CATALOG),
 * so tests can run them against the authored fixtures.
 */
import { BROWSE_CATEGORIES, productsInCategory, type BrowseCategory, type CategoryKey } from './browse';
import type { Product } from './schema';

/** `/gear/<slug>`: the id `brand-slug.model-slug` with its one dot made a hyphen. */
export function productSlug(product: Pick<Product, 'id'> | string): string {
  const id = typeof product === 'string' ? product : product.id;
  return id.replace('.', '-');
}

export function productBySlugIn(products: readonly Product[], slug: string): Product | undefined {
  return products.find((p) => productSlug(p) === slug);
}

/** `/gear/c/<slug>`, one per browse category. Typed by the key union, so a new
 *  category without a slug is a compile error. */
const CATEGORY_SLUGS: Record<CategoryKey, string> = {
  speakers: 'speakers',
  subwoofers: 'subwoofers',
  receivers: 'av-receivers',
  amplifiers: 'power-amplifiers',
  tvs: 'tvs',
  projectors: 'projectors',
  screens: 'projector-screens',
};

export function categorySlug(category: BrowseCategory | CategoryKey): string {
  return CATEGORY_SLUGS[typeof category === 'string' ? category : category.key];
}

export function categoryBySlug(slug: string): BrowseCategory | undefined {
  return BROWSE_CATEGORIES.find((c) => CATEGORY_SLUGS[c.key] === slug);
}

/** The browse category a product is listed under. Every product type belongs to exactly one. */
export function categoryOf(product: Pick<Product, 'type'>): BrowseCategory {
  const found = BROWSE_CATEGORIES.find((c) => c.types.some((t) => t.type === product.type));
  if (!found) throw new Error(`gear pages: no browse category lists type ${product.type}`);
  return found;
}

/**
 * Up to `n` alternatives to `product`: the same type first, then the rest of
 * its category, each ordered by how close the MSRP is (as a ratio, so $300 vs
 * $400 counts the same as $3,000 vs $4,000), then same brand first, then id.
 */
export function similarProducts(products: readonly Product[], product: Product, n: number): Product[] {
  const distance = (p: Product) => Math.abs(Math.log(p.msrpUsd.v / product.msrpUsd.v));
  const order = (a: Product, b: Product) =>
    distance(a) - distance(b) ||
    Number(b.brand === product.brand) - Number(a.brand === product.brand) ||
    a.id.localeCompare(b.id);
  const others = productsInCategory(products, categoryOf(product)).filter((p) => p.id !== product.id);
  const sameType = others.filter((p) => p.type === product.type).sort(order);
  const rest = others.filter((p) => p.type !== product.type).sort(order);
  return [...sameType, ...rest].slice(0, Math.max(0, n));
}

/** The most recent `retrieved` date across a product's sources (YYYY-MM-DD). */
export function lastRetrieved(product: Pick<Product, 'sources'>): string {
  return Object.values(product.sources)
    .map((s) => s.retrieved)
    .sort()
    .at(-1)!;
}
